import React from "react";
import { Link } from "react-router-dom";

import {
  Avatar,
  Divider,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  makeStyles,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import CheckIcon from "@material-ui/icons/Check";
import DoubleCheckIcon from "@material-ui/icons/DoneAll";
import CloseOutlinedIcon from "@material-ui/icons/CloseOutlined";
import { amber, grey } from "@material-ui/core/colors";

import { useOutboundContext } from "./context";
import {
  CarrierType,
  OutboundStatus,
  OutboundState,
} from "../../../utils/constants";
import { accountNameRenderer } from "../../../components/common/AccountNameRenderer";

const useStyles = makeStyles((theme) => ({
  amber: {
    color: theme.palette.getContrastText(amber[500]),
    backgroundColor: amber[500],
  },
  unprocessedItem: {
    color: theme.palette.getContrastText(grey[300]),
    backgroundColor: grey[300],
  },
  link: {
    color: "inherit",
    textDecoration: "inherit",
  },
}));

export const ProcessOutboundShipmentItem = ({
  shipment,
  match,
  idx,
  searchShipments,
}) => {
  const classes = useStyles();
  const xsDown = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const { dispatch } = useOutboundContext();

  const getNumbersText = (shipment, xsDown) => {
    // Transfer scenario
    if (shipment.transfer) {
      let txt = `Transfer to: ${shipment.transferToAccountName}`;
      if (!xsDown && shipment.poNumber) {
        txt += ` - PO #: ${shipment.poNumber}`;
      }
      return txt;
    }

    // Rail vs Truck
    if (shipment.carrierType === CarrierType.RAIL) {
      let txt = `Railcar #: ${shipment.railCarNumber || ""}`.trim();
      if (!xsDown && shipment.poNumber) {
        txt += ` - PO #: ${shipment.poNumber}`;
      }
      return txt;
    }

    if (shipment.carrierType === CarrierType.TRUCK) {
      // For xs: only railCarNumber (trailer) and pickupNumber
      // Larger: add PO Number
      const parts = [];
      if (shipment.pickupNumber)
        parts.push(`Pickup #: ${shipment.pickupNumber}`);
      if (!xsDown && shipment.poNumber)
        parts.push(`PO #: ${shipment.poNumber}`);
      return parts.join(" - ");
    }

    // Fallback: only PO on larger screens
    return !xsDown && shipment.poNumber ? `PO #: ${shipment.poNumber}` : "";
  };

  const getCarrierText = (shipment, xsDown) => {
    if (shipment.carrierType === CarrierType.TRUCK) {
      const parts = [];
      if (!xsDown && shipment.carrierName)
        parts.push(`${shipment.carrierName}`);
      if (shipment.railCarNumber)
        parts.push(`Trl #: ${shipment.railCarNumber}`);

      if (parts.length > 0) return parts.join(" - ");
    }

    return false;
  };

  const numbersText = getNumbersText(shipment, xsDown);
  const carrierText = getCarrierText(shipment, xsDown);

  const getIcon = (shipment) => {
    if (shipment.status === OutboundStatus.LOADED) {
      if (shipment.state === OutboundState.COMPLETED)
        return <DoubleCheckIcon fontSize="inherit" />;
      else return <CheckIcon fontSize="inherit" />;
    }
    return <CloseOutlinedIcon fontSize="inherit" />;
  };

  const getAvatarClassName = (shipment) => {
    if (shipment.hasException) {
      return classes.amber;
    }
    return classes.unprocessedItem;
  };

  const handleSelect = (shipment) => {
    dispatch({ type: "SET_CURRENT_SHIPPMENT", payload: shipment });
  };

  return (
    <Link
      className={classes.link}
      to={match.path + "/" + shipment.id}
      key={shipment.id}
    >
      <ListItem onClick={() => handleSelect(shipment)}>
        <ListItemAvatar>
          <Avatar className={getAvatarClassName(shipment)}>
            {getIcon(shipment)}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={accountNameRenderer(shipment)}
          secondary={
            <>
              <Typography variant="subtitle2" color="textSecondary">
                {numbersText}
              </Typography>
              {carrierText && (
                <Typography variant="subtitle2" color="textSecondary">
                  {carrierText}
                </Typography>
              )}
              {shipment.assignedTo && (
                <Typography variant="subtitle2" color="textSecondary">
                  {shipment.assignedTo}
                </Typography>
              )}
            </>
          }
          primaryTypographyProps={{ display: "block", noWrap: false }}
          secondaryTypographyProps={{
            display: "block",
            noWrap: false,
          }}
        />
        <ListItemSecondaryAction>
          <IconButton edge="end" onClick={() => handleSelect(shipment)}>
            <KeyboardArrowRightIcon fontSize="large" />
          </IconButton>
        </ListItemSecondaryAction>
      </ListItem>
      {idx < searchShipments.length - 1 ? <Divider /> : null}
    </Link>
  );
};
